import { BiRegularUpload, BiSolidXCircle } from "solid-icons/bi";
import { FiAlertCircle } from "solid-icons/fi";
import {
  type Component,
  createSignal,
  createUniqueId,
  type ParentComponent,
  Show,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  UPLOAD_RULES,
  type UploadRule,
} from "@/domains/documents/constants/upload";
import {
  formatUploadedFileSize,
  getAcceptAttribute,
  getAllowedTypesLabel,
  getMaxSizeMb,
  type RejectedUploadFile,
  validateUploadFile,
} from "@/domains/documents/utils/file-validation";
import {
  getUploadApiError,
  mapUploadErrorMessage,
} from "@/domains/documents/utils/upload-error";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import { cn } from "@/libs/cn";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

type TFn = ReturnType<typeof useI18n>["t"];

const clientErrorTitle = (
  t: TFn,
  reason: RejectedUploadFile["reason"],
): string => {
  switch (reason) {
    case "size":
      return t("common.upload_errors.title_file_too_large");
    case "empty":
      return t("common.upload_errors.title_empty_file");
    default:
      return t("common.upload_errors.title_invalid_type");
  }
};

const clientErrorDescription = (
  t: TFn,
  item: RejectedUploadFile,
  typesLabel: string,
  maxSizeMb: number,
): string => {
  switch (item.reason) {
    case "size":
      return t("common.upload_errors.client_too_large", {
        name: item.file.name,
        size: formatUploadedFileSize(item.file),
        max: maxSizeMb,
      });
    case "empty":
      return t("common.upload_errors.client_empty", { name: item.file.name });
    default:
      return t("common.upload_errors.client_invalid_type", {
        name: item.file.name,
        types: typesLabel,
      });
  }
};

const serverErrorTitle = (t: TFn, code: string): string => {
  switch (code) {
    case "LIMIT_FILE_SIZE":
      return t("common.upload_errors.title_file_too_large");
    case "INVALID_FILE_TYPE":
      return t("common.upload_errors.title_invalid_type");
    default:
      return t("common.upload_errors.title_generic");
  }
};

const UploadErrorAlert: Component<{ title: string; description: string }> = (
  props,
) => (
  <Alert variant="destructive" class="text-left">
    <FiAlertCircle size={16} />
    <AlertTitle>{props.title}</AlertTitle>
    <AlertDescription>{props.description}</AlertDescription>
  </Alert>
);

export const DialogImageUploader: ParentComponent<{
  // biome-ignore lint/suspicious/noConfusingVoidType: retro-compatible signature (D12) — sync call sites return void, async ones return a Promise we await for inline BE errors
  onUpload: (image: File) => void | Promise<unknown>;
  uploadRules?: UploadRule;
  /**
   * Extra classes on the trigger button itself.
   *
   * The trigger is a `<button>`, so it is inline-sized by default: a call site
   * whose child is a full-width row (the company review's identity block) has
   * no other way to make the clickable area match what it drew.
   */
  triggerClass?: string;
  /**
   * `data-testid` on the trigger. Needed where a call site draws more than one
   * uploader (the company review: cover strip, then logo row) and a tag query
   * would silently take whichever comes first.
   */
  triggerTestId?: string;
  /**
   * What this uploader writes, used to name its file input. Both inputs used to
   * be `id="file-upload"` outright, so on a page drawing two uploaders the
   * first label opened the second input (AC 29).
   */
  fieldName?: string;
  /**
   * Draws the trigger as the full drop area instead of a plain button, with the
   * same shape the rest of the app uses for file input (`input/resume-input.tsx`).
   *
   * For a surface that is *empty* — no cover yet — a small "Carica" button says
   * nothing about what goes there; the dashed area is the affordance. `children`
   * is ignored in this mode: the content is built from `uploadRules`, so the
   * formats and the weight on screen can never drift from the ones validated.
   */
  dropzone?: boolean;
  /**
   * Replaces the dropzone's first line.
   *
   * The default names the action for a first upload. A call site that has just
   * failed one says so instead — a control that keeps offering the action in
   * exactly the words it used before it went wrong reads as if nothing had
   * happened, and on an empty surface this area is the only affordance there
   * is (FLOW EP10). Ignored outside `dropzone` mode, where the call site owns
   * the label through `children`.
   */
  dropzoneLabel?: string;
  /**
   * Refuses to open at all.
   *
   * A surface can have a second write on the same column — the company cover
   * has a removal beside this upload — and one write at a time is the call
   * site's rule. Staging a file while the other one is in flight would let the
   * confirmation here close the dialog over a write the call site then drops:
   * saying so on the trigger is better than swallowing the click (AC 12).
   */
  disabled?: boolean;
  /**
   * Controlled open state. Omit both of these and the dialog keeps owning it.
   *
   * A call site needs this when the affordance is not the trigger: the company
   * cover opens the picker from an item of the pencil menu drawn over the
   * image, and a menu item cannot *be* a `DialogTrigger` — the menu closes on
   * select and takes any trigger nested inside it down with the dialog.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Draws no trigger at all — for a controlled call site that has its own.
   *
   * Without it `DialogTrigger` would still render around an empty `children`:
   * a focusable button with no accessible name, reachable by Tab and doing
   * nothing visible.
   */
  hideTrigger?: boolean;
}> = (props) => {
  const { t } = useI18n();
  const [uncontrolledOpen, setUncontrolledOpen] = createSignal(false);
  const open = () => props.open ?? uncontrolledOpen();
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    props.onOpenChange?.(next);
  };
  let fileInput: HTMLInputElement | undefined;
  // The field names it; the suffix keeps two uploaders apart even when a call
  // site names them the same, or names neither.
  const uid = createUniqueId();
  const inputId = () => `${props.fieldName ?? "image"}-file-${uid}`;
  const [image, setImage] = createSignal<File | null>(null);
  const [preview, setPreview] = createSignal<string | null>(null);
  const [clientError, setClientError] = createSignal<RejectedUploadFile | null>(
    null,
  );
  const [serverError, setServerError] = createSignal<{
    title: string;
    description: string;
  } | null>(null);
  const [isUploading, setIsUploading] = createSignal(false);

  const rule = () => props.uploadRules ?? UPLOAD_RULES.profileImage;
  const typesLabel = () => getAllowedTypesLabel(rule());

  const handleFile = (file: File) => {
    // Client pre-validation (size + extension); runs for both the picker and the
    // drop path (they share handleFile — R4). Replaces the old generic toast with an
    // inline alert. The alert clears on the next valid selection (AC 4).
    const result = validateUploadFile(file, rule());
    if (!result.ok) {
      setClientError({ file, reason: result.reason });
      setImage(null);
      setPreview(null);
      return;
    }
    setClientError(null);
    setServerError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result && typeof e.target.result === "string") {
        setImage(file);
        setPreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const {
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragAndDrop({
    onFileDrop: handleFile,
  });

  const handleFileInput = (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      handleFile(file);
    }
    // Cleared so picking the same file again — after a rejection, or after a
    // failed upload — still fires `change`.
    if (fileInput) fileInput.value = "";
  };

  const removeImage = () => {
    setImage(null);
    setPreview(null);
  };

  const handleUpload = async () => {
    const file = image();
    if (!file || isUploading()) return; // pending-guard: no double submit (R3)
    setServerError(null);
    setIsUploading(true);
    try {
      // Retro-compatible: sync call sites return void (resolves immediately — the
      // current fire-and-forget behavior); async call sites (T10) return a Promise we
      // await so a mapped 413/415 can surface inline in the still-open dialog.
      await Promise.resolve(props.onUpload(file));
      setPreview(null);
      setImage(null);
      setClientError(null);
      setOpen(false);
    } catch (error) {
      const uploadError = getUploadApiError(error);
      if (uploadError) {
        // Recognized upload error → show the mapped message inline, dialog stays open (D10).
        setServerError({
          title: serverErrorTitle(t, uploadError.code),
          description: mapUploadErrorMessage(uploadError.code, t, {
            allowedTypesLabel: typesLabel(),
            // This surface holds images to 2MB, not the 5MB of a document.
            maxSizeMb: getMaxSizeMb(rule()),
          }),
        });
      } else {
        // Non-upload error → the call site keeps its own fallback (toast); close the
        // dialog as before so the user is never trapped (D10).
        setPreview(null);
        setImage(null);
        setOpen(false);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open()} onOpenChange={setOpen}>
      {/*
        The drag handlers sit on the trigger, not only inside the dialog: a file
        dragged onto what looks like a drop area has to be accepted by it. The
        drop stages the file and opens the dialog on top of it, so there is
        still exactly one place where an upload is confirmed (AC 12).
      */}
      <Show when={!props.hideTrigger}>
        <DialogTrigger
          data-testid={props.triggerTestId}
          disabled={props.disabled}
          class={cn(
            "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
            props.dropzone &&
              "flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors duration-150 ease-out-strong motion-reduce:transition-none focus-visible:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring",
            props.dropzone &&
              (isDragging()
                ? "border-primary bg-primary/5"
                : "border-input hover:border-primary/60 hover:bg-primary/5"),
            props.triggerClass,
          )}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(event: DragEvent) => {
            // A disabled button still receives the drop; refusing it here is what
            // makes `disabled` mean the same thing to the mouse and to the file
            // dragged onto it.
            if (props.disabled) return;
            handleDrop(event);
            setOpen(true);
          }}
        >
          <Show when={props.dropzone} fallback={props.children}>
            <BiRegularUpload
              class="h-10 w-10 text-gray-400 transition-colors motion-reduce:transition-none"
              classList={{ "text-primary": isDragging() }}
            />
            <span class="mt-2 text-sm font-medium text-gray-900">
              {props.dropzoneLabel ?? t("common.image_upload.select_or_drag")}
            </span>
            <span class="mt-1 text-xs text-gray-500">
              {t("common.image_upload.allowed_image_types", {
                types: typesLabel(),
              })}
            </span>
            <span class="mt-1 text-xs text-gray-500">
              {t("common.image_upload.allowed_image_size", {
                size: getMaxSizeMb(rule()),
              })}
            </span>
          </Show>
        </DialogTrigger>
      </Show>
      <DialogContent class="sm:max-w-[425px] bg-card">
        <DialogHeader>
          <DialogTitle>{t("common.image_upload.upload")}</DialogTitle>
        </DialogHeader>
        {/*
          The drop area is a `<button>`, not a decorated `<article>` with a
          `<label>` inside it: a label is not focusable and a `<span
          role="button">` never fires a click on Enter, so both left the picker
          reachable by pointer only. The pattern is `FileInput`'s
          (`input/resume-input.tsx`) — focusable dropzone, `sr-only` input, and
          copy that names the click first, because dragging is the alternative
          (AC 29).
        */}
        <article
          class="mt-4 flex flex-col gap-3"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            id={inputId()}
            ref={fileInput}
            type="file"
            accept={getAcceptAttribute(rule())}
            onChange={handleFileInput}
            class="sr-only"
          />

          <Show
            when={image()}
            fallback={
              <button
                type="button"
                class="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors duration-150 ease-out-strong motion-reduce:transition-none focus-visible:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring"
                classList={{
                  "border-primary bg-primary/5": isDragging(),
                  "border-gray-300 hover:border-primary/60": !isDragging(),
                }}
                onClick={() => fileInput?.click()}
              >
                <BiRegularUpload class="h-12 w-12 text-gray-400" />
                <span class="mt-2 text-sm font-medium text-gray-600">
                  {t("common.image_upload.select_or_drag")}
                </span>
                <span class="mt-1 text-xs text-gray-500">
                  {t("common.image_upload.allowed_image_types", {
                    types: typesLabel(),
                  })}
                </span>
                <span class="mt-1 text-xs text-gray-500">
                  {t("common.image_upload.allowed_image_size", {
                    size: getMaxSizeMb(rule()),
                  })}
                </span>
              </button>
            }
          >
            <div class="relative rounded-lg border-2 border-dashed border-gray-300 p-4">
              <img
                src={preview() ?? ""}
                alt={t("common.image_upload.preview_alt")}
                class="max-w-full h-auto rounded"
              />
              <Button
                variant="destructive"
                size="icon"
                class="absolute top-2 right-2"
                onClick={removeImage}
                disabled={isUploading()}
              >
                <BiSolidXCircle class="h-4 w-4" />
                <span class="sr-only">
                  {t("common.image_upload.removeImage")}
                </span>
              </Button>
            </div>
          </Show>

          <Show when={image()}>
            <Button
              variant="default"
              size="sm"
              class="self-center"
              onClick={handleUpload}
              disabled={isUploading()}
              aria-busy={isUploading()}
            >
              {isUploading()
                ? t("common.image_upload.loading")
                : t("common.image_upload.confirm")}
            </Button>
          </Show>
        </article>

        <Show when={clientError()}>
          {(error) => (
            <UploadErrorAlert
              title={clientErrorTitle(t, error().reason)}
              description={clientErrorDescription(
                t,
                error(),
                typesLabel(),
                getMaxSizeMb(rule()),
              )}
            />
          )}
        </Show>
        <Show when={serverError()}>
          {(error) => (
            <UploadErrorAlert
              title={error().title}
              description={error().description}
            />
          )}
        </Show>
      </DialogContent>
    </Dialog>
  );
};
